import { createAppSelector, sectionSelectors, sectionUserSelectors, termSelectors } from "./base";
import { sortByTermYearRegex } from "./sorting";

import { type TermType } from "components/server-types";
import { compactMap } from "utils/collections";

export const selectSortedTerms = createAppSelector([termSelectors.selectAll], (terms) =>
  terms.toSorted(sortByTermYearRegex),
);

/** Terms that have at least one section. */
export const selectTermsWithSections = createAppSelector(
  [termSelectors.selectAll, sectionSelectors.selectAll],
  (terms, sections): TermType[] => {
    const termIdsWithSections = new Set(compactMap(sections, (section) => section.term_id || null));
    return terms.filter((term) => termIdsWithSections.has(term.id));
  },
);

export const selectTermHasSections = createAppSelector(
  [(state, termId: string) => termId, sectionSelectors.selectAll],
  (termId, sections): boolean => sections.some((section) => section.term_id === termId),
);

export const selectSortedTermsForCurrentUser = createAppSelector(
  [
    sectionUserSelectors.filterForCurrentUser,
    sectionSelectors.selectEntities,
    termSelectors.selectEntities,
  ],
  (sectionUsers, sectionMap, termMap) => {
    const sectionIds = new Set(sectionUsers.map((cu) => cu.section_id));
    const termIds = new Set(
      Array.from(sectionIds).flatMap((sectionId) => {
        const section = sectionMap[sectionId];
        return section ? [section.term_id] : [];
      }),
    );
    return Array.from(termIds)
      .flatMap((termId) => {
        const term = termMap[termId];
        return term ? [term] : [];
      })
      .toSorted(sortByTermYearRegex);
  },
);

/** First sorted term id for the current user, or null if they have none. */
export const selectDefaultTermIdForCurrentUser = createAppSelector(
  [selectSortedTermsForCurrentUser],
  (terms): string | null => terms[0]?.id ?? null,
);
